import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Award, FileDown, ListOrdered, LogOut, Save } from "lucide-react";
import { api, clearSession, dayLabel, errMsg, fmt, getSession, downloadFile } from "@/lib/api";
import { TopBar } from "@/components/fly/Logo";
import { SearchInput, normText } from "@/components/fly/FilterSelect";
import { MembersCell } from "@/components/fly/MembersCell";

const slug = (s) => normText(s).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const eligible = (entries, mode) => entries.some((e) => e.complete && (mode === "all" || (e.place && e.place <= 3)));

const download = async (path, params, name, ok = true) => {
  if (!ok) {
    toast.error("Brak miejsc do wydrukowania – sędziowie nie ocenili jeszcze wszystkich występów.");
    return;
  }
  try {
    await downloadFile({ path, params, filename: `${name}.pdf` });
  } catch (e) {
    const t = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : errMsg(e);
    toast.error(t || "Nie udało się pobrać PDF");
  }
};

const btn = "h-10 px-3 rounded-xl border border-blue-500/30 text-blue-200 hover:bg-blue-500/10 text-sm flex items-center gap-2";
const primary = "h-10 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold flex items-center justify-center gap-2";

export default function Diplomas() {
  const nav = useNavigate();
  const s = getSession();
  const [data, setData] = useState(null);
  const [day, setDay] = useState("");
  const [q, setQ] = useState("");
  const [pd, setPd] = useState("");

  useEffect(() => {
    api.get("/diplomas/results").then((r) => { setData(r.data); setPd(r.data.place_date); setDay(r.data.days[0] || ""); });
  }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    const n = normText(q.trim());
    return data.groups
      .filter((g) => g.day === day)
      .map((g) => (n ? { ...g, shown: g.entries.filter((e) => normText(e.name).includes(n) || normText(e.school).includes(n) || normText(g.category).includes(n)) } : { ...g, shown: g.entries }))
      .filter((g) => g.shown.length);
  }, [data, day, q]);

  const savePd = async () => {
    try { await api.put("/diplomas/settings", { place_date: pd }); toast.success("Zapisano miejsce i datę"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <TopBar title={s?.name} right={
        <button data-testid="dip-logout-btn" onClick={() => { clearSession(); nav("/dyplomy/login"); }} className="h-10 px-3 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-2 text-sm">
          <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Wyloguj</span>
        </button>
      } />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">{s?.name}</p>
        <h1 className="font-display text-4xl font-extrabold uppercase">Zawody</h1>
        {!data ? <p className="text-slate-400 mt-6">Ładowanie…</p> : (
          <>
            <section className="fly-card p-5 mt-5">
              <label className="block text-xs text-slate-400">Miejsce i data na dyplomie
                <div className="flex flex-col sm:flex-row gap-2 mt-1">
                  <input data-testid="dip-place-date-input" value={pd} maxLength={120} onChange={(e) => setPd(e.target.value)} className="w-full sm:flex-1 h-10 px-3 rounded-xl bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none text-sm" />
                  <button data-testid="dip-place-date-save" onClick={savePd} className={primary}><Save className="w-4 h-4" />Zapisz</button>
                </div>
              </label>
            </section>
            <div className="flex flex-wrap gap-2 mt-5" data-testid="dip-day-tabs">
              {data.days.map((d) => (
                <button key={d} data-testid={`dip-day-${slug(d)}`} onClick={() => setDay(d)}
                  className={`h-10 px-4 rounded-full text-sm font-semibold ${day === d ? "bg-blue-600 text-white" : "bg-[#1C2541] text-slate-300 hover:text-white"}`}>{dayLabel(d)}</button>
              ))}
            </div>
            <div className="fly-card p-4 mt-4 flex flex-wrap gap-2" data-testid="dip-day-actions">
              <span className="w-full text-xs font-mono uppercase tracking-wider text-slate-400">Cały dzień: {day}</span>
              <button data-testid="dip-day-winners-btn" onClick={() => download("/diplomas/diplomas.pdf", { day, mode: "winners" }, `dyplomy-1-3-${slug(day)}`, eligible(data.groups.filter((g) => g.day === day).flatMap((g) => g.entries), "winners"))} className={primary}><Award className="w-4 h-4" />Dyplomy 1–3</button>
              <button data-testid="dip-day-all-btn" onClick={() => download("/diplomas/diplomas.pdf", { day, mode: "all" }, `dyplomy-wszyscy-${slug(day)}`, eligible(data.groups.filter((g) => g.day === day).flatMap((g) => g.entries), "all"))} className={btn}><Award className="w-4 h-4" />Dyplomy wszystkich</button>
              <button data-testid="dip-day-results-btn" onClick={() => download("/diplomas/results.pdf", { day }, `wyniki-${slug(day)}`)} className={btn}><ListOrdered className="w-4 h-4" />Lista wyników</button>
            </div>
            <div className="mt-4"><SearchInput testid="dip-search-input" value={q} onChange={setQ} placeholder="Szukaj zawodnika, klubu lub kategorii…" /></div>
            <div className="mt-4 grid grid-cols-1 gap-4 min-w-0">
              {groups.map((g) => <GroupCard key={g.key} g={g} />)}
              {!groups.length && <p className="text-slate-400" data-testid="dip-empty">Brak wyników.</p>}
            </div>
          </>
        )}
      </main>
    </>
  );
}

const GroupCard = ({ g }) => {
  const id = slug(g.key);
  return (
    <section className="fly-card p-4 sm:p-5 min-w-0" data-testid={`dip-group-${id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <div>
          <h2 className="font-display text-xl font-bold uppercase">{g.category}</h2>
          <p className="text-xs font-mono text-blue-400">{g.age_group} · ocenionych {g.scored}/{g.entries.length}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid={`dip-group-winners-${id}`} onClick={() => download("/diplomas/diplomas.pdf", { key: g.key, mode: "winners" }, `dyplomy-1-3-${id}`, eligible(g.entries, "winners"))} className={primary}><Award className="w-4 h-4" />Dyplomy 1–3</button>
          <button data-testid={`dip-group-all-${id}`} onClick={() => download("/diplomas/diplomas.pdf", { key: g.key, mode: "all" }, `dyplomy-wszyscy-${id}`, eligible(g.entries, "all"))} className={btn}><Award className="w-4 h-4" />Wszyscy</button>
          <button data-testid={`dip-group-results-${id}`} onClick={() => download("/diplomas/results.pdf", { key: g.key }, `wyniki-${id}`)} className={btn}><ListOrdered className="w-4 h-4" />Lista</button>
        </div>
      </div>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm">
          <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-400"><th className="py-2 w-12">Msc</th><th>Zawodnik</th><th className="hidden sm:table-cell">Klub</th><th className="text-right">Punkty</th><th className="w-10" /></tr></thead>
          <tbody>
            {g.shown.map((e) => (
              <tr key={e.id} className={`border-t border-white/5 ${e.place && e.place <= 3 ? "text-amber-200 font-semibold" : ""}`} data-testid={`dip-row-${e.id}`}>
                <td className="py-2 font-display text-lg">{e.place || "—"}</td>
                <td><MembersCell e={e} /><div className="sm:hidden text-xs text-slate-400 font-normal">{e.school}</div></td>
                <td className="text-slate-400 hidden sm:table-cell">{e.school}</td>
                <td className="text-right font-mono">{e.complete ? fmt(e.total) : `${e.judges_scored}/${e.judges_total}`}</td>
                <td className="text-right">
                  {e.complete && <button data-testid={`dip-row-pdf-${e.id}`} title="Dyplom" onClick={() => download("/diplomas/diplomas.pdf", { pid: e.id }, `dyplom-${slug(e.name)}`)} className="h-8 w-8 rounded-lg text-blue-300 hover:bg-blue-500/10 inline-flex items-center justify-center"><FileDown className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
